import { ExternalServiceId, RuntimeDate, Uuid } from '@edgebox/data-definition-kit';
import React, { useEffect, useState } from 'react';
import { retrieveEntity } from '../frame-messages';
import { FormatDate } from './FormatDate';

export interface ISiteEntityTypeInfo {
  namespaceMachineName: ExternalServiceId;
  machineName: ExternalServiceId;
}

export async function resolveEntityName(args: {
  namespaceMachineName: ExternalServiceId;
  machineName: ExternalServiceId;
  sharedEntityId: Uuid | ExternalServiceId;
}): Promise<string | undefined> {
  try {
    const entity = await retrieveEntity({
      namespaceMachineName: args.namespaceMachineName,
      machineName: args.machineName,
      sharedEntityId: args.sharedEntityId,
    });

    return entity?.name || undefined;
  } catch {
    return undefined;
  }
}

export function SiteEntityName({
  entityType,
  sharedEntityId,
  pulledAt,
  skipResolve,
  isDeleted,
}: {
  entityType?: ISiteEntityTypeInfo;
  sharedEntityId?: Uuid | ExternalServiceId;
  pulledAt?: RuntimeDate | null;
  skipResolve?: boolean;
  isDeleted?: boolean;
}): React.ReactElement {
  const [name, setName] = useState<string | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!entityType || !sharedEntityId || skipResolve) {
        if (!cancelled) {
          setName(undefined);
          setLoaded(true);
        }
        return;
      }

      setLoaded(false);

      const resolved = await resolveEntityName({
        namespaceMachineName: entityType.namespaceMachineName,
        machineName: entityType.machineName,
        sharedEntityId,
      });

      if (!cancelled) {
        setName(resolved);
        setLoaded(true);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [entityType, sharedEntityId, skipResolve]);

  if (!sharedEntityId) {
    return <em>Unknown</em>;
  }

  if (isDeleted) {
    return (
      <span className="text-muted">
        <em>Deleted</em>
      </span>
    );
  }

  if (!loaded) {
    return (
      <span className="text-muted">
        <em>Loading...</em>
      </span>
    );
  }

  if (!name && pulledAt) {
    return (
      <span className="text-muted">
        <em>
          Clone from <FormatDate withTime>{pulledAt}</FormatDate>
        </em>
      </span>
    );
  }

  return <>{name || <span className="text-muted">{sharedEntityId}</span>}</>;
}
